import { useState } from 'react';
import { BREAKTHROUGH_CARDS, PILLAR_CONFIG } from '../lib/breakthrough';
import type { BreakthroughPillar, BreakthroughCard } from '../lib/breakthrough';
import { ChevronDown, ChevronUp } from 'lucide-react';

type Filter = 'all' | BreakthroughPillar;

export function BreakthroughPage() {
  const [filter, setFilter] = useState<Filter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = filter === 'all'
    ? BREAKTHROUGH_CARDS
    : BREAKTHROUGH_CARDS.filter((c) => c.pillar === filter);

  // Group by category
  const grouped = filtered.reduce<Record<string, BreakthroughCard[]>>((acc, card) => {
    const key = `${card.pillar}|${card.category}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(card);
    return acc;
  }, {});

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-ink">
          Breakthrough!
        </h1>
        <p className="mt-2 max-w-2xl text-ink-secondary">
          Smart, Heart, Courage — the Yum! leadership framework for achieving breakthrough results.
          Use these principles throughout the hackathon to elevate how your team thinks, collaborates, and delivers.
        </p>
      </div>

      {/* Pillar overview cards */}
      <div className="mb-8 grid gap-3 sm:grid-cols-3">
        {(Object.entries(PILLAR_CONFIG) as Array<[BreakthroughPillar, typeof PILLAR_CONFIG['smart']]>).map(
          ([key, pillar]) => (
            <button
              key={key}
              onClick={() => setFilter(filter === key ? 'all' : key)}
              className={`rounded-2xl border p-4 text-left transition-all ${
                filter === key
                  ? `${pillar.bg} ring-2 ring-offset-1 ${key === 'smart' ? 'ring-blue-400' : key === 'heart' ? 'ring-rose-400' : 'ring-amber-400'}`
                  : 'border-ring bg-surface-raised hover:shadow-card-hover'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{pillar.icon}</span>
                <div>
                  <h3 className={`font-semibold ${pillar.color}`}>{pillar.label}</h3>
                  <p className="text-xs text-ink-secondary">{pillar.desc}</p>
                </div>
              </div>
            </button>
          ),
        )}
      </div>

      {/* Filter indicator */}
      {filter !== 'all' && (
        <div className="mb-4 flex items-center gap-2">
          <span className="text-sm text-ink-secondary">
            Showing <strong>{PILLAR_CONFIG[filter].label}</strong> cards
          </span>
          <button onClick={() => setFilter('all')} className="text-xs text-ink-tertiary hover:text-ink-secondary">
            Show all
          </button>
        </div>
      )}

      {/* Cards grouped by category */}
      <div className="space-y-6">
        {Object.entries(grouped).map(([key, cards]) => {
          const [pillarKey, category] = key.split('|');
          const pillar = PILLAR_CONFIG[pillarKey as BreakthroughPillar];

          return (
            <section key={key}>
              <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-ink-secondary">
                <span>{pillar.icon}</span>
                {pillar.label} — {category}
              </h2>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {cards.map((card) => {
                  const isExpanded = expandedId === card.id;
                  return (
                    <button
                      key={card.id}
                      onClick={() => setExpandedId(isExpanded ? null : card.id)}
                      className={`rounded-xl border p-4 text-left transition-all ${pillar.bg} hover:shadow-card-hover`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-sm font-semibold text-ink">{card.title}</h3>
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 shrink-0 text-ink-tertiary" />
                        ) : (
                          <ChevronDown className="h-4 w-4 shrink-0 text-ink-tertiary" />
                        )}
                      </div>
                      {isExpanded && (
                        <div className="mt-2 animate-fade-in">
                          <p className="text-sm leading-relaxed text-ink-secondary">
                            {card.summary}
                          </p>
                          {card.image && (
                            <img
                              src={card.image}
                              alt={card.title}
                              className="mt-3 w-full rounded-lg shadow-sm"
                              loading="lazy"
                            />
                          )}
                        </div>
                      )}
                      {!isExpanded && (
                        <p className="mt-1 text-xs text-ink-tertiary line-clamp-2">
                          {card.summary}
                        </p>
                      )}
                    </button>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      {/* Footer */}
      <p className="mt-10 text-center text-xs text-ink-tertiary">
        Based on Breakthrough! by Business Beyond the Box International & Yum! Brands.
        Smart, Heart, Courage framework.
      </p>
    </div>
  );
}
